// Task execution view component - Mobile-optimized
// CHANGE: Removed all debug console statements for production
// CREATED: Main interface for executing a task with all its items
// UPDATED: Migrated all 4 native alert() calls to useToast notifications for consistent UX
// UPDATED: completeTask now throws on error instead of returning Result object
// UPDATED: Removed handleToggleComplete and updateTaskItemCompletion (individual item completion removed)
// UPDATED: Added "Signaler un incident" shortcut button in the action zone (active missions only)
// UPDATED: Replaced incident CTA button with a discrete text link above the complete button
// UPDATED: Stabilize incident form presentation mode at open time to prevent form state loss on viewport resize
// UPDATED: Explicit three-way conditional for incident form rendering (modal / sheet / null) so IncidentBottomSheet is not mounted when incidentPresentationMode is null

import { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, AlertCircle, Lock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import {
  getAssignedTaskById,
  completeTask,
  updateTaskItemTextInput,
  calculateExecutionSummary,
  AssignedTaskWithDetails
} from '../../lib/execution';
import { supabase } from '../../lib/supabase';
import { TaskItemRow } from './TaskItemRow';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { formatDuration } from '../../lib/dateUtils';
import {
  isTaskEditable,
  type UserRole,
  type TaskEditability
} from '../../lib/taskPermissions';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { IncidentFormModal } from '../incidents/IncidentFormModal';
import { IncidentBottomSheet } from '../incidents/IncidentBottomSheet';

interface TaskExecutionViewProps {
  taskId: string;
  onBack: () => void;
}

export function TaskExecutionView({ taskId, onBack }: TaskExecutionViewProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { isDesktop } = useResponsiveLayout();
  const [task, setTask] = useState<AssignedTaskWithDetails | null>(null);
  const [editability, setEditability] = useState<TaskEditability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [isTaskCompleted, setIsTaskCompleted] = useState(false);
  const [modifiedTextValues, setModifiedTextValues] = useState<Record<string, string>>({});
  const [initialTextValues, setInitialTextValues] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incidentPresentationMode, setIncidentPresentationMode] = useState<'modal' | 'sheet' | null>(null);

  const openIncidentForm = () => {
    setIncidentPresentationMode(isDesktop ? 'modal' : 'sheet');
    setShowIncidentForm(true);
  };

  const closeIncidentForm = () => {
    setShowIncidentForm(false);
  };

  useEffect(() => {
    loadTask();
  }, [taskId]);

  // Reset scroll position when task loads
  useEffect(() => {
    if (task) {
      window.scrollTo(0, 0);
    }
  }, [task]);

  const loadTask = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAssignedTaskById(taskId);

      if (!data) {
        setError('Mission non trouvée');
        return;
      }

      setTask(data);
      setIsTaskCompleted(data.status === 'completed');

      // Check if task is editable based on date and user role
      let taskEditability = null;
      if (user) {
        taskEditability = isTaskEditable(data, user.role as UserRole, user.id);
        setEditability(taskEditability);

        if (!taskEditability.isEditable) {
        }
      }

      // Store initial text values for change detection
      const initialValues: Record<string, string> = {};
      data.items.forEach(item => {
        if (item.requires_text_input) {
          initialValues[item.id] = item.text_value || '';
        }
      });
      setInitialTextValues(initialValues);
      setModifiedTextValues({});
      setHasUnsavedChanges(false);
    } catch (err) {
      setError('Erreur lors du chargement de la mission');
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (itemId: string, value: string) => {
    if (!task || !user) return;

    // Check if task is editable
    if (!editability?.isEditable) {
      // Silent fail for text input - just don't update
      return;
    }

    // Update modified values tracking
    setModifiedTextValues(prev => ({
      ...prev,
      [itemId]: value
    }));

    // Update local task state for validation
    setTask({
      ...task,
      items: task.items.map(item =>
        item.id === itemId
          ? { ...item, text_value: value }
          : item
      )
    });

    // Check if there are actual changes
    const hasChanges = value !== (initialTextValues[itemId] || '');
    if (hasChanges) {
      setHasUnsavedChanges(true);
    } else {
      // Check if any other field has changes
      const otherChanges = Object.keys(modifiedTextValues).some(id =>
        id !== itemId && modifiedTextValues[id] !== (initialTextValues[id] || '')
      );
      setHasUnsavedChanges(otherChanges);
    }
  };

  const saveAllTextInputs = async (): Promise<boolean> => {
    if (!task) return false;

    try {
      // Get only items that have been modified
      const itemsToSave = Object.entries(modifiedTextValues).filter(([itemId, value]) => {
        return value !== (initialTextValues[itemId] || '');
      });

      if (itemsToSave.length === 0) {
        return true; // Nothing to save
      }

      // Save all modified items
      const taskContext = task.isVirtualOccurrence ? {
        isVirtual: true,
        parentTaskId: task.parentTaskId,
        occurrenceDate: task.occurrenceDate
      } : undefined;
      const taskLogId = task.isVirtualOccurrence ? task.parentTaskId : task.id;
      const savePromises = itemsToSave.map(([itemId, value]) =>
        updateTaskItemTextInput(itemId, value, taskContext, { taskId: taskLogId ?? undefined })
      );

      await Promise.all(savePromises);

      // Update initial values to current values after successful save
      const newInitialValues = { ...initialTextValues };
      itemsToSave.forEach(([itemId, value]) => {
        newInitialValues[itemId] = value;
      });
      setInitialTextValues(newInitialValues);
      setModifiedTextValues({});
      setHasUnsavedChanges(false);

      return true;
    } catch (err) {
      showToast('Erreur lors de la sauvegarde des champs texte', 'error');
      return false;
    }
  };


  const handleComplete = async () => {
    if (!task) return;

    setIsCompleting(true);
    setCompleteError(null);

    try {
      const saveSuccess = await saveAllTextInputs();
      if (!saveSuccess) {
        setCompleteError('Erreur lors de la sauvegarde des données');
        setIsCompleting(false);
        return;
      }

      const taskContext = task.isVirtualOccurrence ? {
        isVirtual: true,
        parentTaskId: task.parentTaskId,
        occurrenceDate: task.occurrenceDate
      } : undefined;
      await completeTask(taskId, task.items, taskContext);
      onBack();
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : 'Impossible de terminer la mission');
    } finally {
      setIsCompleting(false);
      setShowCompleteDialog(false);
    }
  };

  const handleBackWithWarning = async () => {
    // If there are unsaved changes, save them automatically
    if (hasUnsavedChanges) {
      const saveSuccess = await saveAllTextInputs();
      if (!saveSuccess) {
        // If save failed, show error but still allow back
        showToast('Erreur lors de la sauvegarde. Les modifications seront perdues.', 'error');
      }
    }
    onBack();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <BouncingDotsLoader size="lg" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
        <p className="text-gray-600">{error || 'Mission non trouvée'}</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Retour
        </button>
      </div>
    );
  }

  const summary = calculateExecutionSummary(task.items);
  const estimatedDuration = task.duration_override_minutes || task.template.estimated_duration_minutes;

  // Format scheduled time for display
  const scheduledTime = task.scheduled_time
    ? task.scheduled_time.substring(0, 5) // Format HH:MM from HH:MM:SS
    : null;

  return (
    <div className="bg-gray-50">
      {/* Mobile Header - Fixed at top */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="py-3 px-4">
          <button
            onClick={handleBackWithWarning}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Retour</span>
          </button>

          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {task.template.name}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              {scheduledTime && (
                <span>
                  Heure : {scheduledTime}
                </span>
              )}
              <span>
                Durée : {formatDuration(estimatedDuration)}
              </span>
            </div>
          </div>
        </div>

        {/* Description section */}
        {task.template.description && task.template.description.trim() && (
          <div className="mb-3 mx-4 px-4 border border-blue-200 rounded-lg bg-blue-50 py-3">
            <div className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">
              {task.template.description}
            </div>
          </div>
        )}

        {/* Read-only notice */}
        {editability && !editability.isEditable && (
          <div className="mb-3 mx-4 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <Lock size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Lecture seule :</strong> {editability.reason}
            </p>
          </div>
        )}
      </header>

      {/* Items List */}
      <main className="py-4">
        <div className="space-y-3">
          {task.items.map((item) => (
            <TaskItemRow
              key={item.id}
              item={item}
              onTextChange={handleTextChange}
              isTaskCompleted={isTaskCompleted}
              isReadOnly={!editability?.isEditable}
            />
          ))}
        </div>

        {/* Action button - inline after mission */}
        {!isTaskCompleted && editability?.isEditable && (
          <div className="mt-6 pb-24 lg:pb-8">
            {/* Error messages */}
            {completeError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{completeError}</p>
              </div>
            )}

            {/* Validation warning */}
            {!summary.canComplete && summary.missingRequiredFields > 0 && (
              <div className="text-xs text-center text-red-600 mb-3">
                <p>
                  {summary.missingRequiredFields} champ(s) obligatoire(s) à remplir
                </p>
              </div>
            )}

            {/* Report incident shortcut */}
            <button
              type="button"
              onClick={openIncidentForm}
              className="w-full min-h-[44px] mb-3 flex items-center justify-center gap-1.5 text-sm text-amber-600 hover:text-amber-800 transition-colors underline underline-offset-2"
            >
              <AlertTriangle size={14} />
              Signaler un incident
            </button>

            {/* Complete button */}
            <button
              onClick={() => {
                setShowCompleteDialog(true);
              }}
              disabled={!summary.canComplete || isCompleting}
              className={`w-full font-semibold rounded-lg py-4 transition-all flex items-center justify-center gap-2 shadow-lg ${
                summary.canComplete && !isCompleting
                  ? 'bg-green-600 text-white hover:bg-green-700 active:scale-98'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <CheckCircle size={22} />
              {isCompleting ? 'Traitement...' : 'Terminer la mission'}
            </button>
          </div>
        )}
      </main>

      {/* Completion Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showCompleteDialog}
        title="Terminer la mission"
        message="Êtes-vous sûr de vouloir terminer cette mission ? Cette action ne peut pas être annulée."
        confirmLabel="Oui, terminer"
        cancelLabel="Annuler"
        onConfirm={handleComplete}
        onCancel={() => {
          setShowCompleteDialog(false);
          setCompleteError(null);
        }}
        variant="info"
      />

      {/* Incident form overlay - presentation locked at open time to prevent remount on viewport resize */}
      {incidentPresentationMode === 'modal' ? (
        <IncidentFormModal
          isOpen={showIncidentForm}
          onClose={closeIncidentForm}
          onSuccess={closeIncidentForm}
        />
      ) : incidentPresentationMode === 'sheet' ? (
        <IncidentBottomSheet
          isOpen={showIncidentForm}
          onClose={closeIncidentForm}
          onSuccess={closeIncidentForm}
        />
      ) : null}
    </div>
  );
}
